import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useWallet } from '@solana/wallet-adapter-react'
import {
  Menu,
  X,
  Home,
  Plus,
  Layers,
  FileText,
  Users as UsersIcon,
  Settings,
  ChevronDown,
  Globe,
  Twitter,
  Send
} from 'lucide-react'
import { cn } from '../lib/utils'
import BackgroundEffects from './BackgroundEffects'
import DevnetBanner from './DevnetBanner'
import WalletConnectButton from './WalletConnectButton'

/**
 * Props interface for the Layout component
 * Defines the structure for the main layout wrapper with enhanced type safety
 */
interface LayoutProps {
  children: React.ReactNode
  className?: string
}

/**
 * Navigation item interface for type safety and consistency
 * Ensures consistent navigation structure across the application
 */
interface NavItem {
  name: string
  href: string
  icon: React.ElementType
  description?: string
  ariaLabel?: string
}

/**
 * Enhanced Layout component with premium glassmorphism navigation
 * 
 * Features:
 * - Glassmorphism navigation with blur effects and smooth transitions
 * - Professional header with animated elements and micro-interactions
 * - Responsive design with mobile-first approach and accessibility
 * - Dynamic background effects integration without conflicts
 * - Enhanced wallet connection displays with proper error handling
 * - WCAG 2.1 AA compliant accessibility features
 * - Optimized performance with memoization and efficient state management
 * - NEW: Custom WalletConnectButton that shows TOS before wallet modal
 */
export function Layout({ children, className }: LayoutProps): JSX.Element {
  // State management for UI interactions
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [_scrolled, setScrolled] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const location = useLocation()
  
  // Safe wallet connection handling with error boundaries
  let connected = false
  let publicKey = null
  let wallet = null
  
  try {
    wallet = useWallet()
    connected = wallet?.connected ?? false
    publicKey = wallet?.publicKey ?? null
  } catch (error) {
    console.error('Layout: Wallet connection error:', error)
    // Graceful degradation - continue with disconnected state
    connected = false
    publicKey = null
  }

  /**
   * Handle scroll events to add dynamic glassmorphism effects to the header
   * Creates responsive styling based on scroll position for enhanced UX
   */
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20
      setScrolled(isScrolled)
    }

    // Passive listeners for better performance
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  /**
   * Check session storage for banner dismissal
   */
  useEffect(() => {
    const dismissed = sessionStorage.getItem('devnet-banner-dismissed') === 'true'
    if (dismissed) {
      setBannerDismissed(true)
    }
  }, [])

  /**
   * Handle banner dismissal
   */
  const handleBannerDismiss = () => {
    setBannerDismissed(true)
    sessionStorage.setItem('devnet-banner-dismissed', 'true')
  }

  /**
   * Handle click outside events to close mobile menus
   * Improves mobile user experience and accessibility
   * Note: Toggle buttons use stopPropagation() to prevent this handler from
   * immediately closing the menu on the same click that opened it
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (mobileMenuOpen && !target.closest('[data-mobile-menu]')) {
        setMobileMenuOpen(false)
      }
      if (userMenuOpen && !target.closest('[data-user-menu]')) {
        setUserMenuOpen(false)
      }
    }

    if (mobileMenuOpen || userMenuOpen) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
    return undefined
  }, [mobileMenuOpen, userMenuOpen])

  /**
   * Navigation items configuration with enhanced accessibility features
   * Updated to match audit requirements: Main, Create Pool, Pools, Roadmap, About Us
   */
  const navigation: NavItem[] = [
    {
      name: 'Main',
      href: '/',
      icon: Home,
      description: 'Main dashboard and overview',
      ariaLabel: 'Go to main page'
    },
    {
      name: 'Create Pool',
      href: '/create-pool',
      icon: Plus,
      description: 'Create a new funding pool',
      ariaLabel: 'Create a new community pool'
    },
    {
      name: 'Pools',
      href: '/pools',
      icon: Layers,
      description: 'Search and explore pools',
      ariaLabel: 'Search for existing pools'
    },
    {
      name: 'WhitePaper',
      href: '/roadmap',
      icon: FileText,
      description: 'WhitePaper & project roadmap',
      ariaLabel: 'View whitepaper and project roadmap'
    },
    {
      name: 'About Us',
      href: '/about',
      icon: UsersIcon,
      description: 'Learn about the team',
      ariaLabel: 'About the CTO Pools team'
    }
  ]

  /**
   * Check if a navigation item is currently active
   * Provides visual feedback for current page location with exact matching
   */
  const isActiveRoute = (href: string): boolean => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  /**
   * Close mobile menu and related overlays
   * Centralized function for consistent behavior
   */
  const closeMobileMenu = () => {
    setMobileMenuOpen(false)
    setUserMenuOpen(false)
  }

  /**
   * Handle keyboard navigation for accessibility compliance
   * Enables proper keyboard support for menu interactions (WCAG 2.1)
   */
  const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      action()
    }
    if (event.key === 'Escape') {
      closeMobileMenu()
    }
  }

  /**
   * Format wallet address for display with proper truncation
   * Shows user-friendly shortened version of the public key
   */
  const formatWalletAddress = (address: string): string => {
    if (!address || address.length < 8) return address
    return `${address.slice(0, 4)}...${address.slice(-4)}`
  }

  /**
   * Get network configuration from environment variables
   * Reads environment config for network, RPC, and program ID
   */
  const getNetworkConfig = () => {
    try {
      // @ts-ignore - Vite env types
      const network = import.meta?.env?.VITE_APP_CLUSTER || 'devnet'
      // @ts-ignore - Vite env types
      const rpcEndpoint = import.meta?.env?.VITE_APP_RPC_URL || 'https://api.devnet.solana.com'
      // @ts-ignore - Vite env types
      const programId = import.meta?.env?.VITE_APP_PROGRAM_ID || ''
      
      return { network, rpcEndpoint, programId }
    } catch (error) {
      console.error('Error reading network config:', error)
      return {
        network: 'devnet',
        rpcEndpoint: 'https://api.devnet.solana.com',
        programId: ''
      }
    }
  }

  const networkConfig = getNetworkConfig()
  const isDevnet = networkConfig.network.toLowerCase() === 'devnet'

  return (
    <>
      {/* Background Effects - Positioned behind everything */}
      <BackgroundEffects
        particleCount={60}
        orbCount={8}
        showGrid={true}
        showAurora={true}
        animationSpeed={0.8}
        enabled={true}
      />

      {/* UI FIX (2026-01-13): Added overflow-x-hidden and max-w-screen to prevent horizontal scrolling on mobile
          This ensures the hamburger menu stays within the viewport bounds */}
      <div className="min-h-screen bg-dark-bg text-text-primary relative overflow-x-hidden max-w-screen" style={{ zIndex: 10 }}>
        {/* Devnet Banner - Shows when connected to devnet */}
        {isDevnet && !bannerDismissed && (
          <DevnetBanner
            dismissible={true}
            onDismiss={handleBannerDismiss}
            showDetails={false}
            network={networkConfig.network}
            rpcEndpoint={networkConfig.rpcEndpoint}
            programId={networkConfig.programId}
            className="mt-16 lg:mt-18"
          />
        )}
        {/* Premium Navigation Header with enhanced accessibility */}
        <header 
          className={cn(
            'fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-cyber',
            'nav-glass border-dark-border/50 shadow-glass-lg backdrop-blur-cyber-xl'
          )}
          role="banner"
        >
          <div className="container mx-auto px-4 lg:px-6">
            <div className="flex items-center justify-between h-16 lg:h-18">
              
              {/* Enhanced Logo and Brand Section */}
              <div className="flex items-center space-x-4">
                <Link 
                  to="/" 
                  className="flex items-center space-x-3 group transform-gpu interactive"
                  aria-label="CTO Pools - Return to homepage"
                >
                  <div className={cn(
                    "relative w-10 h-10 lg:w-12 lg:h-12 rounded-cyber overflow-hidden",
                    "bg-cyber-gradient shadow-neon-cyan-lg group-hover:shadow-neon-cyan",
                    "transition-all duration-300 group-hover:scale-110"
                  )}>
                    <img 
                      src="/logoblack.png"
                      alt="CTO Pools Logo"
                      className="w-8 h-8 lg:w-9 lg:h-9 absolute inset-0 m-auto object-contain"
                    />

                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-40" />
                  </div>
                  
                  <div className="hidden sm:block">
                    <h1 className={cn(
                      "font-header text-xl lg:text-2xl font-bold",
                      "text-gradient-premium animate-gradient-x",
                      "group-hover:text-glow-strong transition-all duration-300"
                    )}>
                      CTO Pools
                    </h1>
                    <p className="text-xs lg:text-sm text-text-muted font-medium">
                      Community Takeovers
                    </p>
                  </div>
                </Link>

                {/* Network Status Indicator - Enhanced for accessibility */}
                <div className="hidden lg:flex items-center space-x-2 px-3 py-1.5 glass-dark rounded-cyber">
                  <div
                    className={cn(
                      "w-2 h-2 rounded-full pulse-glow",
                      isDevnet ? "bg-status-warning" : "bg-status-success"
                    )}
                    aria-hidden="true"
                  />
                  <span
                    className={cn(
                      "text-xs font-mono font-semibold",
                      isDevnet ? "text-status-warning" : "text-status-success"
                    )}
                    aria-label={`Connected to Solana ${networkConfig.network}`}
                  >
                    {networkConfig.network.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Desktop Navigation - Enhanced with ARIA labels */}
              <nav 
                className="hidden lg:flex items-center space-x-2" 
                role="navigation" 
                aria-label="Main navigation"
              >
                {navigation.map((item) => {
                  const isActive = isActiveRoute(item.href)
                  const IconComponent = item.icon
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        'relative flex items-center space-x-2 px-4 py-2.5 rounded-cyber transition-all duration-300',
                        'hover:bg-dark-panel/50 hover:text-cyber-cyan hover:shadow-neon-cyan',
                        'focus-cyber interactive',
                        isActive
                          ? 'bg-dark-panel/80 text-cyber-cyan border border-cyber-cyan/30 shadow-neon-cyan'
                          : 'text-text-secondary border border-transparent hover:border-cyber-cyan/20'
                      )}
                      aria-label={item.ariaLabel}
                      aria-current={isActive ? 'page' : undefined}
                      onKeyDown={(e) => handleKeyDown(e, () => {})}
                    >
                      <IconComponent className="w-4 h-4" aria-hidden="true" />
                      <span className="font-medium">{item.name}</span>
                      
                      {/* Hover glow effect - decorative only */}
                      <div 
                        className="absolute inset-0 rounded-cyber bg-cyber-gradient opacity-0 hover:opacity-10 transition-opacity duration-300 pointer-events-none" 
                        aria-hidden="true"
                      />
                    </Link>
                  )
                })}
              </nav>

              {/* Right Side Actions - Enhanced accessibility */}
              <div className="flex items-center space-x-3">
                
                {/* Wallet Connection Status Display */}
                {connected && publicKey && (
                  <div 
                    className="hidden md:flex items-center space-x-2 px-3 py-2 glass-dark rounded-cyber"
                    role="status"
                    aria-label={`Wallet connected: ${formatWalletAddress(publicKey.toBase58())}`}
                  >
                    <div 
                      className="w-2 h-2 bg-status-success rounded-full animate-pulse" 
                      aria-hidden="true"
                    />
                    <span className="text-xs font-mono text-status-success">
                      {formatWalletAddress(publicKey.toBase58())}
                    </span>
                  </div>
                )}

                {/* Custom Wallet Connect Button - Shows TOS before wallet modal */}
                <WalletConnectButton
                  connectLabel="Select Wallet"
                  variant="primary"
                  size="md"
                  showDropdown={true}
                />

                {/* User Menu (when connected) - Enhanced accessibility */}
                {connected && (
                  <div className="relative" data-user-menu>
                    <button
                      onClick={(e) => {
                        // Stop propagation to prevent click-outside handler from
                        // immediately closing the menu on the same event
                        e.stopPropagation()
                        setUserMenuOpen(!userMenuOpen)
                      }}
                      onKeyDown={(e) => handleKeyDown(e, () => setUserMenuOpen(!userMenuOpen))}
                      className={cn(
                        "flex items-center space-x-2 px-3 py-2 rounded-cyber",
                        "glass-dark hover:bg-dark-panel/70 transition-all duration-300",
                        "focus-cyber interactive"
                      )}
                      aria-expanded={userMenuOpen}
                      aria-haspopup="menu"
                      aria-label="User account menu"
                    >
                      <div className="w-6 h-6 bg-cyber-gradient rounded-full" aria-hidden="true" />
                      <ChevronDown className={cn(
                        "w-4 h-4 transition-transform duration-300",
                        userMenuOpen && "rotate-180"
                      )} aria-hidden="true" />
                    </button>

                    {/* User Dropdown Menu - Enhanced with ARIA */}
                    {userMenuOpen && (
                      <div 
                        className="absolute right-0 top-full mt-2 w-48 glass-card border-cyber-cyan/20 rounded-cyber shadow-glass-lg overflow-hidden"
                        role="menu"
                        aria-label="User account options"
                      >
                        <div className="p-3 border-b border-dark-border/50">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-cyber-gradient rounded-full" aria-hidden="true" />
                            <div>
                              <div className="text-sm font-medium text-text-primary">Connected</div>
                              <div className="text-xs text-text-muted font-mono">
                                {publicKey && formatWalletAddress(publicKey.toBase58())}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="py-1" role="group">
                          <button 
                            className="w-full px-3 py-2 text-left text-sm text-text-secondary hover:bg-dark-panel/50 hover:text-cyber-cyan transition-colors duration-200 flex items-center space-x-2"
                            role="menuitem"
                          >
                            <Settings className="w-4 h-4" aria-hidden="true" />
                            <span>Settings</span>
                          </button>
                          <button 
                            className="w-full px-3 py-2 text-left text-sm text-text-secondary hover:bg-dark-panel/50 hover:text-cyber-cyan transition-colors duration-200 flex items-center space-x-2"
                            role="menuitem"
                          >
                            <Globe className="w-4 h-4" aria-hidden="true" />
                            <span>Network</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Mobile menu button - Enhanced accessibility with minimum 44x44px touch target */}
                <button
                  type="button"
                  className={cn(
                    "lg:hidden p-3 rounded-cyber transition-all duration-300 min-w-[44px] min-h-[44px]",
                    "glass-dark hover:bg-dark-panel/70 focus-cyber",
                    "flex items-center justify-center",
                    mobileMenuOpen
                      ? "bg-dark-panel/80 text-cyber-cyan shadow-neon-cyan"
                      : "text-text-secondary"
                  )}
                  onClick={(e) => {
                    // Stop propagation to prevent click-outside handler from
                    // immediately closing the menu on the same event
                    e.stopPropagation()
                    setMobileMenuOpen(!mobileMenuOpen)
                  }}
                  onKeyDown={(e) => handleKeyDown(e, () => setMobileMenuOpen(!mobileMenuOpen))}
                  aria-expanded={mobileMenuOpen}
                  aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                  aria-controls="mobile-navigation"
                  data-mobile-menu
                >
                  {mobileMenuOpen ? (
                    <X className="w-5 h-5" aria-hidden="true" />
                  ) : (
                    <Menu className="w-5 h-5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            {/* Enhanced Mobile Navigation Menu - Improved accessibility */}
            {mobileMenuOpen && (
              <div 
                className="lg:hidden border-t border-dark-border/50 animate-slide-down"
                id="mobile-navigation"
                data-mobile-menu
              >
                <nav className="py-4 space-y-2" role="navigation" aria-label="Mobile navigation">
                  {navigation.map((item) => {
                    const isActive = isActiveRoute(item.href)
                    const IconComponent = item.icon
                    
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={cn(
                          'flex items-center space-x-3 px-4 py-3 transition-all duration-300 rounded-cyber mx-2',
                          'hover:bg-dark-panel/50 hover:text-cyber-cyan focus-cyber',
                          isActive
                            ? 'bg-dark-panel/80 text-cyber-cyan border-l-2 border-cyber-cyan'
                            : 'text-text-secondary'
                        )}
                        onClick={closeMobileMenu}
                        aria-label={item.ariaLabel}
                        aria-current={isActive ? 'page' : undefined}
                        onKeyDown={(e) => handleKeyDown(e, closeMobileMenu)}
                      >
                        <IconComponent className="w-5 h-5" aria-hidden="true" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{item.name}</span>
                          </div>
                          {item.description && (
                            <div className="text-xs text-text-muted mt-0.5" aria-hidden="true">
                              {item.description}
                            </div>
                          )}
                        </div>
                      </Link>
                    )
                  })}

                  {/* Mobile Wallet Status - Enhanced display */}
                  {connected && publicKey && (
                    <div className="mx-4 mt-4 pt-4 border-t border-dark-border/50">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-3 h-3 bg-status-success rounded-full animate-pulse" 
                          aria-hidden="true"
                        />
                        <div>
                          <div className="text-sm font-medium text-status-success">
                            Wallet Connected
                          </div>
                          <div className="text-xs text-text-muted font-mono">
                            {formatWalletAddress(publicKey.toBase58())}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </nav>
              </div>
            )}
          </div>
        </header>

        {/* Main Content Area - Enhanced semantic structure with proper padding */}
        <main 
          className={cn(
            "relative min-h-screen",
            (isDevnet && !bannerDismissed) ? "pt-4" : "pt-20 lg:pt-24",
            className
          )}
          style={{ zIndex: 20 }}
          role="main"
          aria-label="Main content"
        >
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            {children}
          </div>
        </main>

        {/* Enhanced Footer - Improved accessibility and structure */}
        <footer 
          className="relative border-t border-dark-border/50 mt-auto" 
          style={{ zIndex: 20 }}
          role="contentinfo"
        >
          <div className="glass-dark backdrop-blur-cyber-xl">
            <div className="container mx-auto px-4 lg:px-6 py-12">
              <div className="grid md:grid-cols-3 gap-8">
                
                {/* Brand & Description */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-cyber-gradient rounded-cyber flex items-center justify-center">
                      <img 
                        src="/logoblack.png"
                        alt="CTO Pools"
                        className="w-6 h-6 object-contain brightness-0"
                      />
                    </div>
                    <span className="font-header text-lg text-gradient-cyber">CTO Pools</span>
                  </div>
                  <p className="text-sm text-text-secondary leading-relaxed max-w-sm">
                    Decentralized community-driven funding platform for abandoned token takeovers on Solana. 
                    Empowering communities through transparent governance.
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-text-muted">
                    <span>Made with ❤️ for Solana and Blockchain</span>
                  </div>

                  {/* Social Links */}
                  <div className="flex items-center space-x-4 pt-2">
                    <a
                      href="https://x.com/CtoPools"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-cyber bg-dark-panel border border-dark-border flex items-center justify-center text-text-secondary hover:text-cyber-cyan hover:border-cyber-cyan transition-all duration-300 group"
                      aria-label="Follow us on X (formerly Twitter)"
                    >
                      <Twitter className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    </a>
                    <a
                      href="https://t.me/ctopools"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-cyber bg-dark-panel border border-dark-border flex items-center justify-center text-text-secondary hover:text-cyber-cyan hover:border-cyber-cyan transition-all duration-300 group"
                      aria-label="Join our Telegram community"
                    >
                      <Send className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    </a>
                  </div>
                </div>

                {/* Quick Links - Enhanced for screen readers */}
                <div className="space-y-4">
                  <h3 className="font-header text-cyber-cyan">Quick Links</h3>
                  <nav className="space-y-2" aria-label="Footer navigation">
                    {navigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="block text-sm text-text-secondary hover:text-cyber-cyan transition-colors duration-200"
                        aria-label={item.ariaLabel}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </nav>
                </div>

                {/* Technical Info - Enhanced structure */}
                <div className="space-y-4">
                  <h3 className="font-header text-cyber-cyan">Built With</h3>
                  <ul className="space-y-2" role="list">
                    {[
                      { name: 'Solana Blockchain', color: 'cyber-purple' },
                      { name: 'Anchor Framework', color: 'cyber-green' },
                      { name: 'React + TypeScript', color: 'cyber-cyan' }
                    ].map((tech) => (
                      <li key={tech.name} className="flex items-center space-x-2 text-sm">
                        <div 
                          className={`w-2 h-2 bg-${tech.color}-500 rounded-full`} 
                          aria-hidden="true"
                        />
                        <span className="text-text-secondary">{tech.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Bottom Bar - Enhanced semantic structure */}
              <div className="mt-8 pt-6 border-t border-dark-border/30 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                <p className="text-sm text-text-muted">
                  © 2026 CTO Pools and Invent LLC. Open source and community driven.
                </p>
                <div className="flex items-center space-x-4 text-text-muted text-sm">
                  <Link to="/terms" className="hover:text-cyber-cyan transition-colors">
                    Terms of Service
                  </Link>
                  <span aria-hidden="true">•</span>
                  <span>Version 1.0.1</span>
                  <span aria-hidden="true">•</span>
                  <span>Devnet</span>
                </div>
              </div>
            </div>
          </div>
        </footer>

        {/* Mobile menu backdrop - Enhanced accessibility */}
        {(mobileMenuOpen || userMenuOpen) && (
          <div 
            className="fixed inset-0 z-40 bg-dark-bg/50 backdrop-blur-sm lg:hidden animate-backdrop-in"
            onClick={closeMobileMenu}
            onKeyDown={(e) => handleKeyDown(e, closeMobileMenu)}
            aria-hidden="true"
            role="presentation"
          />
        )}
      </div>
    </>
  )
}

/**
 * Higher-Order Component for pages that need layout wrapper
 * Provides a clean way to wrap components with the Layout
 * Maintains component display names for better debugging
 */
export function withLayout<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  const WrappedComponent = (props: P) => (
    <Layout>
      <Component {...props} />
    </Layout>
  )
  
  WrappedComponent.displayName = `withLayout(${Component.displayName || Component.name})`
  return WrappedComponent
}

export default Layout
